<script setup lang="ts">
  import { computed, useCssModule, useSlots } from 'vue';

  export interface PageHeaderProps {
    /**
     * The title of the page.
     */
    title?: string;
    /**
     * The description of the page.
     */
    description?: string;
  }

  defineOptions({
    name: 'll-page-header',
  });

  const props = withDefaults(defineProps<PageHeaderProps>(), {
    title: undefined,
    description: undefined,
  });

  const classes = useCssModule();
  const slots = useSlots();

  const hasActions = computed<boolean>(() => !!(slots['primary-action'] || slots['secondary-action']));
</script>

<template>
  <header
    class="stash-page-header"
    :class="[classes.root, { [classes.grid]: hasActions }]"
    data-test="stash-page-header"
  >
    <div class="col-span-full flex items-center lg:col-span-1">
      <div v-if="slots.media" class="stash-page-header__media mr-3 self-center" :class="classes.mediaTest">
        <!-- @slot Receives a custom media -->
        <slot name="media"></slot>
      </div>

      <div>
        <!-- @slot Receives a custom title -->
        <slot name="title">
          <h1 v-if="props.title" class="stash-page-header__title my-0">{{ props.title }}</h1>
        </slot>

        <!-- @slot Receives a custom description -->
        <slot name="description">
          <p v-if="props.description" class="stash-page-header__description mb-0 mt-1.5">
            {{ props.description }}
          </p>
        </slot>
      </div>
    </div>

    <div v-if="hasActions" class="stash-page-header__actions col-span-full flex lg:col-span-1" :class="classes.actions">
      <!-- @slot Receives a custom secondary action -->
      <slot name="secondary-action"> </slot>
      <!-- @slot Receives a custom primary action -->
      <slot name="primary-action"> </slot>
    </div>
  </header>
</template>

<style module>
  @reference "../../../styles/main.css";

  .root {
    border-bottom: 0;
    display: grid;
    gap: var(--grid-gutter);
    margin: 0 auto;
    max-width: var(--width-container);
    padding: --spacing(6) var(--grid-gutter) --spacing(12);
    width: 100%;
  }

  @media (width >= theme(--breakpoint-lg)) {
    .root.grid {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  .actions {
    align-self: flex-end;
    flex-direction: column-reverse;
    row-gap: var(--grid-gutter);
  }

  @media (width >= theme(--breakpoint-md)) {
    .actions {
      column-gap: var(--grid-gutter);
      flex-direction: row;
      row-gap: 0;
    }
  }

  @media (width >= theme(--breakpoint-lg)) {
    .actions {
      align-items: flex-end;
      flex-direction: row;
      justify-content: flex-end;
    }
  }

  /* max size for media/marketing illustration icons we would be passing to the slot */
  .media-test {
    max-width: 58px;
    max-height: 58px;
  }
</style>
